import { createAsyncThunk } from '@reduxjs/toolkit'
import { getData, getLocalUrl } from './request'

export const MIN_DAEMON_API_VERSION = 1

export const DAEMON_DOWNLOAD_URLS = {
    windows: '/connectivity_c.exe',
    linux: '/connectivity_c.lin',
}

const DAEMON_PLATFORM_LABELS = {
    windows: 'Windows',
    linux: 'Linux',
}

export function daemonDownloadLinks() {
    return [
        { key: 'windows', label: DAEMON_PLATFORM_LABELS.windows, href: DAEMON_DOWNLOAD_URLS.windows },
        { key: 'linux', label: DAEMON_PLATFORM_LABELS.linux, href: DAEMON_DOWNLOAD_URLS.linux },
    ]
}

// Platform detection by UA/`navigator.platform`. Returns a single {url,href,label}
// for the unambiguous Windows/Linux cases and null otherwise (macOS, mobile,
// unknown) — callers then offer links to both binaries instead of guessing.
export function detectDaemonDownloadUrl() {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
    const platform = typeof navigator !== 'undefined' ? navigator.platform || '' : ''

    const isWindows = /Windows|Win32|Win64|WinNT/i.test(ua) || /^Win/i.test(platform)
    const isLinux = /Linux/i.test(ua) && !/Android/i.test(ua)

    if (isWindows)
        return { url: DAEMON_DOWNLOAD_URLS.windows, href: DAEMON_DOWNLOAD_URLS.windows, label: DAEMON_PLATFORM_LABELS.windows }
    if (isLinux)
        return { url: DAEMON_DOWNLOAD_URLS.linux, href: DAEMON_DOWNLOAD_URLS.linux, label: DAEMON_PLATFORM_LABELS.linux }
    return null
}

// Classifies a GET /version response into { daemonApi, daemonApiCompatibility }.
// - integer `api` present → graded against MIN_DAEMON_API_VERSION
// - any other reachable body (legacy daemon `{"result":-1,...}`, foreign
//   service on the port) → 'unknown'
export function classifyDaemonResponse(json) {
    if (!json || typeof json !== 'object' || !Number.isInteger(json.api))
        return { daemonApi: null, daemonApiCompatibility: 'unknown' }

    const daemonApi = json.api
    return {
        daemonApi,
        daemonApiCompatibility: daemonApi >= MIN_DAEMON_API_VERSION ? 'ok' : 'incompatible',
    }
}

// Always resolves (never rejects): timeout/network failures are the same
// 'unknown' classification as an unverifiable body, so a transient poll failure
// cannot wedge the store. The entry gate awaits a fresh dispatch each time.
export const asyncCheckDaemonCompatibility = createAsyncThunk(
    'asyncCheckDaemonCompatibility',
    async () => {
        try {
            return classifyDaemonResponse(await getData(getLocalUrl('version')))
        } catch (error) {
            return { daemonApi: null, daemonApiCompatibility: 'unknown' }
        }
    }
)

export function isTrainingBlocked(connectivity) {
    if (!connectivity) return false
    const compatibility = connectivity.daemonApiCompatibility
    return compatibility === 'incompatible' || compatibility === 'unknown'
}

export function buildDaemonBlockMessage(verdict, detected) {
    const version = verdict && verdict.daemonApi != null
        ? ` (API v${verdict.daemonApi})`
        : ''
    const lead = verdict && verdict.daemonApi != null
        ? `Версия API локального сервиса${version} ниже требуемой v${MIN_DAEMON_API_VERSION}.`
        : 'Не удалось проверить версию локального сервиса.'
    const action = detected
        ? `Скачайте и запустите актуальную версию для ${detected.label}: ${detected.href}`
        : `Скачайте и запустите актуальную версию: Windows — ${DAEMON_DOWNLOAD_URLS.windows}, Linux — ${DAEMON_DOWNLOAD_URLS.linux}`
    return `${lead} ${action}`
}

// Dispatches a fresh compatibility check and maps the outcome to a verdict the
// UI can act on. `dispatch` is injected by the store wrapper so this stays
// free of circular imports and testable with a stub.
export async function assertDaemonCompatible(dispatch) {
    let verdict
    try {
        verdict = await dispatch(asyncCheckDaemonCompatibility()).unwrap()
    } catch (error) {
        verdict = { daemonApi: null, daemonApiCompatibility: 'unknown' }
    }

    if (verdict.daemonApiCompatibility === 'ok')
        return { ok: true, message: '' }

    const detected = detectDaemonDownloadUrl()
    return {
        ok: false,
        message: buildDaemonBlockMessage(verdict, detected),
        download: detected,
        daemonApi: verdict.daemonApi,
    }
}